import { z, ZodObject, ZodSchema } from "zod";

export interface EmbryoUploadBase {
  condition: keyof typeof EmbryoCondition | null;
  hpi: number | null;
  fileUrl: string;
  label: string;
  originalName: string;
  path?: string;
  signedUrl?: string;
  status: "init" | "uploading" | "uploaded" | "error";
}

export interface EmbryoUploadImage extends EmbryoUploadBase {
  kind: "image",
}

export interface EmbryoUploadVideo extends EmbryoUploadBase {
  kind: "video",
  currentTime: number;
  selectedTime: number | null;
  thumb: string | null;
}

export type EmbryoUpload = EmbryoUploadImage | EmbryoUploadVideo;

export interface FormData {
  patient: string;
  birth: Date;
  transfer?: Date | null;
  fertilization?: "ivf" | "icsi" | null;
}

export interface FormResponse {
  cycle: { id: string; created: string };
}

export interface GetCycleResponseImage {
  id: number;
  created: string;
  updated: string;
  filename: string;
  hpi: number;
  frozen: boolean;
  p_aneuploidy: number;
  p_confidence: number;
  p_implantation: number;
  p_livebirth: number;
  p_overall: number;
  u_notes: string;
  u_overall: number;
  invalid: boolean;
}

export enum EmbryoCondition {
  fresh = "fresh",
  frozen = "frozen",
}

export enum FertilizationMethod {
  "IVF" = 0,
  "ICSI" = 1,
}

export interface GetCycleResponseItem {
  id: string;
  patient: string;
  birth: string;
  u_transfer_date: string | null;
  transfer: string | null;
  u_transfer_outcome: number;
  u_livebirth: number;
  p_livebirth: number;
  s_livebirth: number;
  p_overall: number;
  images: GetCycleResponseImage[];
  created: string;
  updated: string;
  fertilization: FertilizationMethod;
}

export interface GetCycleResponse {
  cycle: GetCycleResponseItem;
}

export const schema: ZodSchema<FormData> = z.object({
  patient: z.string({ message: "errors.patient.required" }).min(2).max(32),
  birth: z
    .date({ message: "errors.birth.invalid" })
    .max(new Date(new Date().setFullYear(new Date().getFullYear() - 16)), {
      message: "errors.birth.max",
    })
    .min(new Date(new Date().setFullYear(new Date().getFullYear() - 50)), {
      message: "errors.birth.min",
    }),
  transferDate: z.date().optional().nullable(),
  fertilizationMethod: z.enum(Object.keys(FertilizationMethod) as [keyof typeof FertilizationMethod]).optional(),
});

const uploadObject = z.object({
  condition: z.enum(
    Object.keys(EmbryoCondition) as [keyof typeof EmbryoCondition]
  ).nullable(),
  fileUrl: z.string(),
  hpi: z.number().min(64).max(128).nullable(),
  kind: z.literal("image"),
  label: z.string(),
  originalName: z.string(),
  signedUrl: z.string().optional(),
  status: z.enum(["init", "uploading", "uploaded", "error"]),
});

const imageSchema = uploadObject.merge(z.object({
  kind: z.literal("image"),
}));

const videoSchema = uploadObject.merge(z.object({
  kind: z.literal("video"),
  currentTime: z.number(),
  selectedTime: z.number().nullable(),
  thumb: z.string().nullable(),
}));

export const mediaSchema: ZodSchema<EmbryoUpload[]> = z
  .array(
    z.discriminatedUnion("kind", [imageSchema, videoSchema])
  )
  .min(1, { message: "form.errors.image.upload" });
